import { useCallback, useMemo, useRef, useState } from 'react';
import {
  DataTableExpandedRows,
  DataTableValue,
  DataTableValueArray,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { noop } from 'lodash';
import { StepComponent } from '../types';
import { useForm } from '../../../../components/Form/hooks';
import {
  DEFAULT_VALUES,
  FormTalhao,
  TALHAO_VALIDATION_SCHEMA,
} from './form-controller';
import Form from './form';
import RowExpansionTemplate from './row-expansion-template';
import ActionButtonsTemplate from '../components/action-buttons-template';
import { TalhaoRow } from '../../../../@types';
import { dateColumnTemplate, numberColumnTemplate } from '../utils';
import AutoSortTable from '../../../../components/AutoSortTable';
import { AtividadesService } from '../../../../services';
import defaultColumnProps from '../../../../utils/default-column-props';
import Agro1Toast from '../../../../components/Agro1Toast';
import { validationSchema } from '../form-controller';

const Talhoes: StepComponent = ({ formObject, recomendacaoSemeadura }) => {
  const [talhoes, sementeCobertura, atividade] = formObject.form.watch([
    'talhoes',
    'sementeCobertura',
    'atividade',
  ]);
  const [editingTalhaoIdentification, setEditingTalhaoIdentification] =
    useState<string | null>(null);

  const contextForm = useMemo(
    () => ({
      talhoes,
      editingTalhaoIdentification,
      sementeCobertura,
      atividade,
    }),
    [talhoes, editingTalhaoIdentification, sementeCobertura, atividade],
  );

  const formTalhao = useForm<TalhaoRow>({
    validationSchema: TALHAO_VALIDATION_SCHEMA,
    defaultValues: DEFAULT_VALUES,
    context: contextForm,
  });
  const { reset } = formTalhao.form;
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [selectedTalhao, setSelectedTalhao] = useState<DataTableValue>();
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >();

  const edit = useCallback(
    (data: TalhaoRow) => {
      reset(data, { keepDefaultValues: true });
      formContainerRef.current?.scrollIntoView();
      setEditingTalhaoIdentification(data.identification);
      setSelectedTalhao(data);
    },
    [reset],
  );

  const remove = useCallback(
    (identification: string | null) => {
      if (!identification) return;

      const filteredTalhoes = formObject.form
        .getValues('talhoes')
        .filter((item) => item.identification !== identification);

      formObject.form.setValue('talhoes', filteredTalhoes, {
        shouldValidate: true,
      });

      const [talhaoId] = identification.split('-');
      const isTalhaoInCultivares = filteredTalhoes.find(
        ({ talhao }) => talhao?.id === +talhaoId,
      );

      const adubos = formObject.form.getValues('adubos');
      if (adubos.length && !isTalhaoInCultivares) {
        const filteredAdubos = adubos.filter(
          (adubo) => adubo.talhao?.talhao?.id !== +talhaoId,
        );

        formObject.form.setValue('adubos', filteredAdubos, {
          shouldValidate: true,
        });
      }

      Agro1Toast.show({
        detail: 'Registro excluído com sucesso.',
        severity: 'success',
      });
    },
    [formObject.form],
  );

  const ActionBodyTemplate = useCallback(
    (rowData: TalhaoRow) => (
      <ActionButtonsTemplate
        rowData={rowData}
        disabled={!!editingTalhaoIdentification}
        onEdit={edit}
        onRemove={remove}
      />
    ),
    [editingTalhaoIdentification, edit, remove],
  );

  const rowExpansionTemplate = useCallback(
    (data: TalhaoRow) => {
      const showPMS = !AtividadesService.isMilho(atividade?.descricao ?? '');
      return RowExpansionTemplate(data, showPMS, sementeCobertura);
    },
    [atividade, sementeCobertura],
  );

  const onSubmitHandler = () => {
    const data = formTalhao.form.getValues();

    if (!data.talhao || !data.itemSaldo) return;

    const itens = [...formObject.form.getValues('talhoes')];
    const newTalhao = {
      ...data,
      identification: `${data.talhao.id}-${data.itemSaldo.id}`,
    };

    if (editingTalhaoIdentification) {
      const index = itens.findIndex(
        ({ identification }) => identification === editingTalhaoIdentification,
      );

      itens[index] = newTalhao;
      formObject.form.setValue('talhoes', itens, { shouldValidate: true });

      clearSubForm();

      Agro1Toast.show({
        detail: 'Registro alterado com sucesso.',
        severity: 'success',
      });

      return;
    }

    formObject.form.setValue('talhoes', [...itens, newTalhao], {
      shouldValidate: true,
    });

    clearSubForm();

    Agro1Toast.show({
      detail: 'Registro salvo com sucesso.',
      severity: 'success',
    });
  };

  const clearSubForm = () => {
    reset(DEFAULT_VALUES);
    setEditingTalhaoIdentification(null);
    setSelectedTalhao(undefined);
  };

  return (
    <FormTalhao formObject={formTalhao} onSubmit={noop}>
      <div ref={formContainerRef}>
        <Form
          formObject={formObject}
          editingTalhaoIdentification={editingTalhaoIdentification}
          onCancel={clearSubForm}
          recomendacaoSemeadura={recomendacaoSemeadura}
          onSubmit={onSubmitHandler}
        />
      </div>
      <div className="recomendacoes-semeadura-create-edit-talhoes">
        <AutoSortTable
          removableSort
          sortField="dataInicio"
          dataKey="identification"
          value={talhoes}
          className="mt-5 p-0 data-table"
          emptyMessage="Nenhum talhão adicionado."
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          selectionMode="single"
          selection={selectedTalhao ?? null}
        >
          <Column expander className="row-expander" />
          <Column
            {...defaultColumnProps('left')}
            field="talhao.descricao"
            header="Talhão"
          />
          <Column
            {...defaultColumnProps('left')}
            field="itemSaldo.descricao"
            header="Cultivar"
          />
          <Column
            {...defaultColumnProps()}
            field="area"
            header="Área (ha)"
            body={numberColumnTemplate}
          />
          <Column
            {...defaultColumnProps()}
            field="dataInicio"
            header="Data Início"
            body={dateColumnTemplate}
          />
          <Column
            {...defaultColumnProps()}
            field="dataTermino"
            header="Data Término"
            body={dateColumnTemplate}
          />
          <Column align="center" body={ActionBodyTemplate} />
        </AutoSortTable>
      </div>
    </FormTalhao>
  );
};

Talhoes.validationSchema = validationSchema.pick(['talhoes']);

export default Talhoes;
